import React, { useState } from 'react';
import { useStudioIQAPI } from '../services/studioiqAPI';
import { useAuth } from '@clerk/clerk-react';

/**
 * API Test Page - Development component to test StudioIQ API integration
 * This component will be removed in production
 */
const APITestPage = () => {
  const { isSignedIn } = useAuth();
  const studioiqAPI = useStudioIQAPI();
  const [testResults, setTestResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [testInput, setTestInput] = useState('14k white gold engagement ring needs sizing from 6 to 7.5 and 2 prongs retipped');

  const addTestResult = (testName, result) => {
    setTestResults(prev => ({
      ...prev,
      [testName]: {
        ...result,
        timestamp: new Date().toISOString()
      }
    }));
  };

  const testAuthCheck = async () => {
    setLoading(true);
    try {
      const result = await studioiqAPI.authCheck();
      addTestResult('authCheck', { success: true, data: result });
    } catch (error) {
      addTestResult('authCheck', { success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const testParseJob = async () => {
    setLoading(true);
    try {
      const result = await studioiqAPI.parseJob(testInput);
      addTestResult('parseJob', { success: true, data: result });
    } catch (error) {
      addTestResult('parseJob', { success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const testQuoteJob = async () => {
    setLoading(true);
    try {
      // Mock structured job for testing
      const mockJob = {
        jewelryType: 'Ring',
        repairType: 'Sizing',
        subtype: 'Resize Up',
        itemDimensions: 'Size 6 to 7.5',
        totalStones: 1,
        metalType: 'Gold',
        metalColor: 'White',
        metalTier: '14K',
        complexityTier: 'Moderate',
        repairId: 'RSZ001',
        requiresParts: false,
        description: 'Resize engagement ring up 1.5 sizes'
      };

      const result = await studioiqAPI.quoteJob(mockJob);
      addTestResult('quoteJob', { success: true, data: result });
    } catch (error) {
      addTestResult('quoteJob', { success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const testHealthCheck = async () => {
    setLoading(true);
    try {
      const result = await studioiqAPI.healthCheck();
      addTestResult('healthCheck', { success: true, data: result });
    } catch (error) {
      addTestResult('healthCheck', { success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setTestResults({});
  };

  if (!isSignedIn) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h2 className="text-xl font-bold text-yellow-800 mb-2">API Test Page</h2>
        <p className="text-yellow-700">Please sign in to test API integration</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h2 className="text-2xl font-bold text-blue-800 mb-2">StudioIQ API Test Page</h2>
        <p className="text-blue-700">Development component to test Cloudflare Workers integration</p>
      </div>

      {/* Test Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Test Input (for parseJob):
        </label>
        <textarea
          value={testInput}
          onChange={(e) => setTestInput(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="3"
          placeholder="Enter jewelry repair description..."
        />
      </div>

      {/* Test Buttons */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <button
          onClick={testAuthCheck}
          disabled={loading}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
        >
          Test Auth Check
        </button>
        <button
          onClick={testParseJob}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          Test Parse Job
        </button>
        <button
          onClick={testQuoteJob}
          disabled={loading}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
        >
          Test Quote Job
        </button>
        <button
          onClick={testHealthCheck}
          disabled={loading}
          className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
        >
          Test Health Check
        </button>
      </div>

      {/* Clear Results Button */}
      <div className="mb-6">
        <button
          onClick={clearResults}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          Clear Results
        </button>
      </div>

      {/* Loading Indicator */}
      {loading && (
        <div className="mb-4 p-4 bg-gray-100 border border-gray-300 rounded-md">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
            <span className="text-gray-700">Testing API...</span>
          </div>
        </div>
      )}

      {/* Test Results */}
      <div className="space-y-4">
        {Object.entries(testResults).map(([testName, result]) => (
          <div key={testName} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-800">{testName}</h3>
              <div className="flex items-center">
                {result.success ? (
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">
                    ✅ Success
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm">
                    ❌ Failed
                  </span>
                )}
                <span className="text-xs text-gray-500 ml-2">
                  {new Date(result.timestamp).toLocaleTimeString()}
                </span>
              </div>
            </div>
            
            {result.success ? (
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <pre className="text-sm text-green-800 overflow-x-auto">
                  {JSON.stringify(result.data, null, 2)}
                </pre>
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="text-sm text-red-800 font-medium">Error:</p>
                <p className="text-sm text-red-700">{result.error}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* API Documentation */}
      <div className="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">API Endpoints</h3>
        <div className="space-y-2 text-sm">
          <div><strong>parse-job:</strong> https://parse-job.studioiq.workers.dev</div>
          <div><strong>quote-job:</strong> https://quote-job.studioiq.workers.dev</div>
          <div><strong>auth-check:</strong> https://auth-check.studioiq.workers.dev</div>
        </div>
      </div>
    </div>
  );
};

export default APITestPage;